import React, { useCallback } from 'react'

import * as typography from 'assets/styles/typography'
import {
    AnimatedField,
    FormikInput,
} from 'components/FormikFields/FormikTextArea/FormikTextArea.styled'
import styled from 'styled-components'

import {
    FieldConfigBase,
    FieldKit,
    FieldProps,
    FormValues,
} from '../../containers/FormBuilder'

export const Subtitle = styled.div`
    ${typography.Copy16}
    font-size: .75em;
    margin-bottom: 1em;
    color: #30333a;
`
export type TextAreaFieldOnChange = (
    value: string,
    fieldName: string,
    formValues: FormValues,
    fieldKit: FieldKit,
) => void

interface TextAreaFieldConfig extends FieldConfigBase {
    label: string
    resize?: boolean
    disabled?: boolean
    placeholder?: string
    subtitle?: string | React.ReactNode
    onChange?: TextAreaFieldOnChange
    formatFn?: (value: string) => string
}

export default function TextAreaField(
    props: Readonly<FieldProps<TextAreaFieldConfig>>,
) {
    const { fieldName, fieldKit, fieldConfig } = props
    const value = fieldKit.values[fieldName]

    const handleBlur = useCallback(
        (event: React.FocusEvent<HTMLTextAreaElement>) => {
            fieldKit.handleBlur(event)

            let formattedValue = (value ?? '').trim()

            if (fieldConfig.formatFn) {
                formattedValue = fieldConfig.formatFn(formattedValue)
            }

            if (value !== formattedValue) {
                fieldKit.setFieldValue(fieldName, formattedValue)
            }
        },
        [fieldConfig, fieldKit, fieldName, value],
    )

    return (
        <div>
            <AnimatedField>
                <FormikInput
                    resize={fieldConfig.resize}
                    disabled={fieldConfig.disabled}
                    id={fieldName}
                    name={fieldName}
                    label={fieldConfig.label}
                    value={value}
                    onChange={(val: string) => {
                        if (fieldConfig.onChange) {
                            fieldConfig.onChange(
                                val,
                                fieldName,
                                fieldKit.values,
                                fieldKit,
                            )
                        } else {
                            const event = {
                                target: {
                                    name: fieldName,
                                    value: val,
                                },
                            }

                            fieldKit.handleChange(event)
                        }
                    }}
                    onBlur={handleBlur}
                    placeholder={fieldConfig.placeholder}
                    data-testid={fieldName}
                />
            </AnimatedField>
            {fieldConfig.subtitle && (
                <Subtitle className="no-print">{fieldConfig.subtitle}</Subtitle>
            )}
        </div>
    )
}

TextAreaField.defaultProps = {
    resize: true,
    disabled: false,
    placeholder: '',
}
